import { useState } from "react";

import { Icon } from "@iconify-icon/react";
import { cn } from "utils";

import { SearchIcon } from "Layout/icons/SearchIcon";
import DashboardLayout from "Layout/DashboardLayout";
import Button from "Components/Button";
import { Card } from "Components/Card";
import Loader from "Components/Loader";
import AddProductsToCategoryModal from "./Components/AddProductsToCategoryModal";
import { Separator } from "Components/Separator";
import { formatCurrency } from "utils/formatNumber";
import { useDebounce } from "hooks/useDebounce";
import { useProducts } from "hooks/ProductsManagement/useGetProductsQuery";
import { useAddProductsToCategoryMutation } from "hooks/CategoryManagement/useAddProductsToCategoryMutation";

const AddProductToCategory = () => {
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<any | null>(null);
  const [selectedCategoryName, setSelectedCategoryName] = useState<
    string | null
  >(null);

  const debounceSearch = useDebounce(search);

  const [cartItems, setCartItems] = useState<any[]>([]);
  const [products, setProducts] = useState<string[]>([]);

  const { data } = useProducts({
    search: debounceSearch,
  });

  const hasSelectedProduct = cartItems.length > 0;

  const toggleProductInCart = (product: { id: string }) => {
    const exists = cartItems.some((item) => item.id === product.id);

    if (exists) {
      setCartItems(cartItems.filter((item) => item.id !== product.id));
      setProducts((prevProducts) =>
        prevProducts.filter((id) => id !== product.id)
      );
    } else {
      setCartItems([...cartItems, product]);
      setProducts((prevProducts) => [...prevProducts, product.id]);
    }
  };

  const clearCartItems = () => {
    setCartItems([]);
    setProducts([]);
  };

  const { mutateAsync, isPending } =
    useAddProductsToCategoryMutation(selectedCategory);

  const handleSubmit = () => {
    mutateAsync(products);
  };

  return (
    <DashboardLayout title="Add Product To Category" back={true}>
      <div className="p-4">
        <div className="flex gap-[30px]">
          <div className="w-8/12 mt-4">
            <div className="border border-[#eeeeee] p-3 rounded-[10px]">
              <div className="flex items-center justify-between">
                <span className="font-medium cursor-pointer">All Product</span>
                <div className="">
                  <div
                    className={
                      "flex gap-1 items-center relative max-h-[44px] min-h-[44px] bg-white w-full border duration-300 border-[#d3d3d3] focus:border-primary-light rounded-md"
                    }
                  >
                    <div className="flex justify-center items-center px-3">
                      <SearchIcon className="relative top-0 left-0" />
                    </div>
                    <input
                      type="text"
                      placeholder={"Search for product"}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="w-full h-full bg-transparent outline-none border-none flex-1"
                    />
                  </div>
                </div>
              </div>
            </div>
            {isPending ? (
              <Loader />
            ) : (
              <div className="grid gap-y-4 gap-x-3 grid-cols-3 mt-3">
                {data?.data?.docs.map((product) => {
                  const itemExists = cartItems.some(
                    (item) => item.id === product.id
                  );

                  return (
                    <div
                      className={cn("cursor-pointer")}
                      onClick={() => toggleProductInCart(product)}
                      key={product.id}
                    >
                      <img
                        src={
                          product.images[0] || "https://via.placeholder.com/50"
                        }
                        alt="product"
                        className={cn(
                          "w-full h-[200px] border-[0.86px] border-[#eeeeee] rounded-lg",
                          {
                            "border-primary border-[2px]": itemExists,
                          }
                        )}
                      />
                      <h3 className="mt-4 font-normal text-xs">
                        {product.name}
                      </h3>
                      <div className="my-2 font-semibold text-sm">
                        {formatCurrency(product.bulkPrice)}
                      </div>
                      <p className="mb-1 text-xs">
                        &#x20A6; {product.unitPrice.toLocaleString()}{" "}
                        <span className="text-ash">unit price</span>
                      </p>
                      <div className="text-primary-foreground text-xs flex gap-x-1">
                        <div>{product.bulkTotalStock} Cartons left</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div className="w-4/12 mt-5">
            <Card className="text-base font-medium flex justify-between items-center min-h-16">
              <h6 className="">
                Selected Product(s){" "}
                {hasSelectedProduct ? `(${cartItems.length})` : ""}
              </h6>
              {hasSelectedProduct && (
                <div>
                  <Button
                    label={
                      <div className="flex gap-x-1 items-center">
                        <Icon icon="hugeicons:delete-02" width={20} />
                        Clear
                      </div>
                    }
                    onClick={clearCartItems}
                    className="bg-primary-light text-[#414141] p-3 text-sm font-medium"
                  />
                </div>
              )}
            </Card>
            <Card className="text-base font-medium h-[29rem] p-0 overflow-y-auto mt-3">
              {cartItems.map((item, index) => (
                <div key={item.id}>
                  <div className="flex items-center justify-between p-4">
                    <img
                      src={item.images[0] || "https://via.placeholder.com/50"}
                      alt="selected product"
                      className="w-[50px] h-[50px] rounded-lg"
                    />
                    <div className="text-sm font-medium">
                      {item.name} - {formatCurrency(item.bulkPrice)}
                    </div>
                  </div>
                  {index < cartItems.length - 1 && <Separator />}
                </div>
              ))}
            </Card>
            <Card className="text-base font-medium p-0 overflow-y-auto mt-3">
              <div className="p-3 flex justify-between">
                <h3>Select Category</h3>
                <AddProductsToCategoryModal
                  selectedCategory={selectedCategory}
                  setSelectedCategory={setSelectedCategory}
                  selectedCategoryName={selectedCategoryName}
                  setSelectedCategoryName={setSelectedCategoryName}
                />
              </div>
            </Card>
            <div className="pt-3">
              <Button
                label="Submit"
                disabled={cartItems.length === 0 || !selectedCategory}
                onClick={handleSubmit}
                isLoading={isPending}
              />
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AddProductToCategory;
